import { useMemo } from "react";
import Alert from "react-bootstrap/Alert";
import { resources } from "@constants/hardcoded-strings";
import { LabelWidthTypes } from "@enums/label-width-types";
import { LayoutTypes } from "@enums/layout-types";
import { SelectOption } from "@idw-types/common";
import { PolicyDetailErrors } from "@idw-types/common/validation/verification-service-details-validation-errors";
import { RpServiceConfigurationPolicy } from "@idw-types/data/rp-service-configuration";
import { AxnRpService } from "@idw-types/data/axn-rp-service";
import { getAxnRpServiceOptions, getObligationStepUpToList, isObligationApiKeyCorrect } from "@utils/policies/policy-util";
import { SelectFormGroup } from "../../../../../common/select-form-group/select-form-group";
import { SimpleRPServiceWorkflow } from "@idw-types/data/reference-data/axn/simple-rp-service-workflow";

type AlwaysObligatePolicyDetailsContainerProps = {
    isDisabled: boolean;
    validationErrors: PolicyDetailErrors;
    axnRpServices: Array<AxnRpService>;
    rpServiceID: string;
    workflowID: string;
    simpleRPServiceWorkflows: Array<SimpleRPServiceWorkflow>;
    policies: Array<RpServiceConfigurationPolicy>;
    updatePolicies: (policies: Array<RpServiceConfigurationPolicy>) => void;
};

export const AlwaysObligatePolicyDetailsContainer = (props: AlwaysObligatePolicyDetailsContainerProps) => {
    const {
        isDisabled,
        rpServiceID,
        workflowID,
        axnRpServices,
        policies,
        simpleRPServiceWorkflows,
        validationErrors,
        updatePolicies
    } = props;

    const handleStepUpApiKeyChange = (newValue: SelectOption) => {
        const newPolicies = (policies || []).map(policy => ({
            ...policy,
            properties: (policy.properties || []).map(property => ({
                ...property,
                value: (property.name || "").toLowerCase() === "obligation.apikey" ? newValue.value : property.value
            }))
        }));
        updatePolicies(newPolicies);
    };

    const obligationStepUpList = useMemo(() => {
        if (axnRpServices.length > 0) {
            return getObligationStepUpToList(rpServiceID, workflowID, axnRpServices, simpleRPServiceWorkflows);
        }

        return [];
    }, [axnRpServices, rpServiceID, workflowID, simpleRPServiceWorkflows]);

    const selectedApiKey = useMemo(() => {
        if (policies && policies.length > 0) {
            const policy = policies[0];
            if (policy.properties && policy.properties.length > 0) {
                const property = policy.properties.find(p => (p.name || "").toLowerCase() === "obligation.apikey");
                if (property?.value) {
                    return property.value as string;
                }
            }
        }
        return null;
    }, [policies]);

    const axnRpServiceOptions = useMemo(() => {
        return getAxnRpServiceOptions(obligationStepUpList, selectedApiKey, axnRpServices);
    }, [obligationStepUpList, selectedApiKey, axnRpServices]);


    const selectedStepUpApiKeyOption = useMemo(() => {
        if (selectedApiKey) {
            const option = axnRpServiceOptions.find(apiKeyOption => apiKeyOption.value === selectedApiKey);
            if (option) {
                return option;
            }
        }
        return null;
    }, [axnRpServiceOptions, selectedApiKey]);

    const shouldShowObligationWarningMessage = useMemo(() => {
        if (obligationStepUpList.length && selectedApiKey) {
            return !isObligationApiKeyCorrect(selectedApiKey, obligationStepUpList);
        }

        return false;
    }, [obligationStepUpList, selectedApiKey]);

    return (
        <div>
            {!!selectedApiKey && !selectedStepUpApiKeyOption && (
                <Alert variant="danger">{resources.pages.verificationServiceDetails.tabs.policies.sections.details.sections.alwaysObligate.invalidStepUpApiKey}</Alert>
            )}
            <SelectFormGroup
                controlId={"policies-tab-always-obligate-step-up-api-key-select-input"}
                automationId={"policies-tab-always-obligate-step-up-api-key-select-input"}
                label={resources.pages.verificationServiceDetails.tabs.policies.sections.details.sections.alwaysObligate.inputs.stepUpApiKey.label}
                ariaLabel={resources.pages.verificationServiceDetails.tabs.policies.sections.details.sections.alwaysObligate.inputs.stepUpApiKey.label}
                disabled={isDisabled}
                isClearable={false}
                isRequired={true}
                isSearchable={true}
                layout={LayoutTypes.ROW}
                labelWidth={LabelWidthTypes.MEDIUM}
                onChange={handleStepUpApiKeyChange}
                options={axnRpServiceOptions}
                selectedOption={selectedStepUpApiKeyOption}
            />
            {shouldShowObligationWarningMessage && <Alert variant={"warning"}>{resources.pages.verificationServiceDetails.tabs.policies.sections.details.obligationWarningMessage}</Alert>}
            {validationErrors.noStepUpApiKeyError}
        </div>
    );
};